import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Search, Bed, Bath, Square, Car, Plus, Pencil, Trash2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

function formatPrice(price) {
  if (price == null) return '—';
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(price);
}

function PropertyTypeIcon({ type }) {
  const icons = { detached: '🏠', condo: '🏢', townhouse: '🏘️', semi: '🏡' };
  return <span className="property-type-icon">{icons[type] || '🏠'}</span>;
}

const STATUS_BADGE_CLASS = { active: 'active', pending: 'qualified', sold: 'closed', inactive: 'closed' };

const SHOWING_DAYS = [
  ['mon', 'Mon'],
  ['tue', 'Tue'],
  ['wed', 'Wed'],
  ['thu', 'Thu'],
  ['fri', 'Fri'],
  ['sat', 'Sat'],
  ['sun', 'Sun'],
];

function hoursToForm(hours) {
  const form = {};
  SHOWING_DAYS.forEach(([key]) => {
    const window = Array.isArray(hours?.[key]) ? hours[key][0] : '';
    const [start, end] = String(window || '').split('-');
    form[key] = {
      enabled: Boolean(start && end),
      start: start || '10:00',
      end: end || '16:00',
    };
  });
  return form;
}

function formToHours(form) {
  const hours = {};
  SHOWING_DAYS.forEach(([key]) => {
    const day = form[key];
    hours[key] = day?.enabled && day.start && day.end
      ? [`${day.start.slice(0, 5)}-${day.end.slice(0, 5)}`]
      : [];
  });
  return hours;
}

const EMPTY_FORM = {
  address_line1: '', city: '', province: 'NL', postal_code: '',
  list_price: '', bedrooms: '', bathrooms: '', square_feet: '',
  property_type: 'detached', status: 'active', mls_number: '',
  listing_agent_name: '', public_remarks: '', agent_house_notes: '',
  showing_availability: '', garage: false,
};

/** Round up to a step sized to the number's own magnitude (50K under $1M, 100K above). */
function niceRoundUp(n) {
  if (!n || n <= 0) return 0;
  const step = n < 1000000 ? 50000 : 100000;
  return Math.ceil(n / step) * step;
}

export default function Properties() {
  const location = useLocation();
  const [properties, setProperties] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // Unfiltered snapshot used only to derive filter options — the filtered `properties`
  // list would otherwise shrink the city/price options as soon as one is applied.
  const [allCities, setAllCities] = useState([]);
  const [priceCeil, setPriceCeil] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState({ city: '', min_price: '', max_price: '', bedrooms: '' });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [sellerHoursOn, setSellerHoursOn] = useState(false);
  const [sellerHoursForm, setSellerHoursForm] = useState(() => hoursToForm({}));
  const [saving, setSaving] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    api.get('/properties?limit=200')
      .then(res => {
        const all = res.data.properties || [];
        setAllCities(Array.from(new Set(all.map(p => p.city).filter(Boolean))).sort());
        setPriceCeil(all.reduce((m, p) => Math.max(m, Number(p.list_price) || 0), 0));
      })
      .catch(console.error);
  }, []);

  const priceBandOptions = [0.25, 0.5, 0.75, 1]
    .map((frac) => niceRoundUp(priceCeil * frac))
    .filter((v, i, arr) => v > 0 && arr.indexOf(v) === i);

  const propertyParams = (offset) => {
    const params = new URLSearchParams();
    if (filter.city) params.append('city', filter.city);
    if (filter.min_price) params.append('min_price', filter.min_price);
    if (filter.max_price) params.append('max_price', filter.max_price);
    if (filter.bedrooms) params.append('bedrooms', filter.bedrooms);
    params.append('limit', '60');
    params.append('offset', String(offset));
    return params;
  };

  const fetchProperties = () => {
    api.get(`/properties?${propertyParams(0)}`)
      .then(res => {
        setProperties(res.data.properties || []);
        setHasMore(Boolean(res.data.has_more));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const loadMoreProperties = () => {
    setLoadingMore(true);
    api.get(`/properties?${propertyParams(properties.length)}`)
      .then(res => {
        const more = res.data.properties || [];
        setProperties((prev) => [...prev, ...more]);
        setHasMore(Boolean(res.data.has_more));
      })
      .catch(console.error)
      .finally(() => setLoadingMore(false));
  };

  useEffect(() => { fetchProperties(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showForm && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showForm, editingId]);

  useEffect(() => {
    if (location.state?.view !== 'list') return;
    setShowForm(false);
    setEditingId(null);
    window.scrollTo({ top: 0 });
  }, [location.state]);

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setSellerHoursOn(false);
    setSellerHoursForm(hoursToForm({}));
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      address_line1: p.address_line1 || '',
      city: p.city || '',
      province: p.province || 'NL',
      postal_code: p.postal_code || '',
      list_price: p.list_price ?? '',
      bedrooms: p.bedrooms ?? '',
      bathrooms: p.bathrooms ?? '',
      square_feet: p.square_feet ?? '',
      property_type: p.property_type || 'detached',
      status: p.status || 'active',
      mls_number: p.mls_number || '',
      listing_agent_name: p.listing_agent_name || '',
      public_remarks: p.public_remarks || '',
      agent_house_notes: p.agent_house_notes || '',
      showing_availability: p.showing_availability || '',
      garage: !!p.garage,
    });
    const hours = p.showing_hours && typeof p.showing_hours === 'object' ? p.showing_hours : {};
    const enabled = SHOWING_DAYS.some(([key]) => Array.isArray(hours[key]) && hours[key].length);
    setSellerHoursOn(enabled);
    setSellerHoursForm(hoursToForm(hours));
    setShowForm(true);
  };

  const setField = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.address_line1 || !form.city) {
      toast.error('Address and city are required');
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      list_price: form.list_price === '' ? null : Number(form.list_price),
      bedrooms: form.bedrooms === '' ? null : Number(form.bedrooms),
      bathrooms: form.bathrooms === '' ? null : Number(form.bathrooms),
      square_feet: form.square_feet === '' ? null : Number(form.square_feet),
      showing_hours: sellerHoursOn ? formToHours(sellerHoursForm) : null,
    };
    try {
      if (editingId) {
        await api.patch(`/properties/${editingId}`, payload);
        toast.success('Listing updated');
      } else {
        await api.post('/properties', payload);
        toast.success('Listing added');
      }
      setShowForm(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      fetchProperties();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save listing');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete listing at ${p.address_line1}? This can't be undone.`)) return;
    try {
      await api.delete(`/properties/${p.id}`);
      toast.success('Listing deleted');
      setProperties(ps => ps.filter(x => x.id !== p.id));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete listing');
    }
  };

  const filtered = properties.filter(p =>
    search === '' ||
    p.address_line1?.toLowerCase().includes(search.toLowerCase()) ||
    p.city?.toLowerCase().includes(search.toLowerCase()) ||
    p.mls_number?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="page-header" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
        <div>
          <h2>Properties</h2>
          <p>{properties.length} listings. Your own homes are marked My listing. Click one to change the price, status, remarks, or the seller&apos;s hours.</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>
          <Plus size={15} /> Add Listing
        </button>
      </div>

      {showForm && (
        <div className="card" style={{marginBottom: 24}} ref={formRef}>
          <div className="card-header">
            <span className="card-title">{editingId ? (form.address_line1 || 'Listing') : 'New listing'}</span>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowForm(false)}>
              <X size={14} /> Cancel
            </button>
          </div>
          <form onSubmit={handleSave}>
            {editingId ? (
              <div style={{marginBottom: 8}}>
              <div style={{fontWeight: 700, marginBottom: 12}}>On the listing</div>
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px 20px'}}>
                {[
                  ['Address', form.address_line1],
                  ['City', [form.city, form.province, form.postal_code].filter(Boolean).join(', ')],
                  ['MLS', form.mls_number],
                  ['Bedrooms', form.bedrooms],
                  ['Bathrooms', form.bathrooms],
                  ['Square feet', form.square_feet ? Number(form.square_feet).toLocaleString('en-CA') : ''],
                  ['Type', ({ detached: 'Detached', semi: 'Semi-detached', townhouse: 'Townhouse', condo: 'Condo', other: 'Other' })[form.property_type] || form.property_type],
                  ['Garage', form.garage ? 'Yes' : 'No'],
                  ['Listing agent', form.listing_agent_name],
                ].map(([label, value]) => (
                  <div key={label} style={{minWidth: 0}}>
                    <div style={{fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-muted)'}}>{label}</div>
                    <div style={{marginTop: 2}}>{value || '—'}</div>
                  </div>
                ))}
              </div>
              </div>
            ) : (
            <>
            <div className="grid-2" style={{marginBottom: 12}}>
              <div className="form-group" style={{marginBottom: 0}}>
                <label className="form-label" htmlFor="prop-address">Address</label>
                <input id="prop-address" className="form-input" value={form.address_line1} onChange={e => setField('address_line1', e.target.value)} required />
              </div>
              <div className="form-group" style={{marginBottom: 0}}>
                <label className="form-label" htmlFor="prop-city">City</label>
                <input id="prop-city" className="form-input" value={form.city} onChange={e => setField('city', e.target.value)} required />
              </div>
            </div>
            <div className="grid-2" style={{marginBottom: 12}}>
              <div className="form-group" style={{marginBottom: 0}}>
                <label className="form-label" htmlFor="prop-province">Province</label>
                <input id="prop-province" className="form-input" value={form.province} onChange={e => setField('province', e.target.value)} />
              </div>
              <div className="form-group" style={{marginBottom: 0}}>
                <label className="form-label" htmlFor="prop-postal-code">Postal Code</label>
                <input id="prop-postal-code" className="form-input" value={form.postal_code} onChange={e => setField('postal_code', e.target.value)} />
              </div>
            </div>
            <div className="grid-2" style={{marginBottom: 12}}>
              <div className="form-group" style={{marginBottom: 0}}>
                <label className="form-label" htmlFor="prop-list-price">List Price (CAD)</label>
                <input id="prop-list-price" className="form-input" type="number" min="0" value={form.list_price} onChange={e => setField('list_price', e.target.value)} />
              </div>
              <div className="form-group" style={{marginBottom: 0}}>
                <label className="form-label" htmlFor="prop-mls-number">MLS #</label>
                <input id="prop-mls-number" className="form-input" value={form.mls_number} onChange={e => setField('mls_number', e.target.value)} />
              </div>
            </div>
            <div className="grid-2" style={{marginBottom: 12}}>
              <div className="form-group" style={{marginBottom: 0}}>
                <label className="form-label" htmlFor="prop-bedrooms">Bedrooms</label>
                <input id="prop-bedrooms" className="form-input" type="number" min="0" value={form.bedrooms} onChange={e => setField('bedrooms', e.target.value)} />
              </div>
              <div className="form-group" style={{marginBottom: 0}}>
                <label className="form-label" htmlFor="prop-bathrooms">Bathrooms</label>
                <input id="prop-bathrooms" className="form-input" type="number" min="0" step="0.5" value={form.bathrooms} onChange={e => setField('bathrooms', e.target.value)} />
              </div>
            </div>
            <div className="grid-2" style={{marginBottom: 12}}>
              <div className="form-group" style={{marginBottom: 0}}>
                <label className="form-label" htmlFor="prop-square-feet">Square Feet</label>
                <input id="prop-square-feet" className="form-input" type="number" min="0" value={form.square_feet} onChange={e => setField('square_feet', e.target.value)} />
              </div>
              <div className="form-group" style={{marginBottom: 0}}>
                <label className="form-label" htmlFor="prop-property-type">Type</label>
                <select id="prop-property-type" className="form-input" value={form.property_type} onChange={e => setField('property_type', e.target.value)}>
                  <option value="detached">Detached</option>
                  <option value="semi">Semi-detached</option>
                  <option value="townhouse">Townhouse</option>
                  <option value="condo">Condo</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            <div className="form-group" style={{marginBottom: 12}}>
              <label className="form-label" style={{display: 'flex', alignItems: 'center', gap: 8, textTransform: 'none'}}>
                <input
                  type="checkbox"
                  checked={form.garage}
                  onChange={e => setField('garage', e.target.checked)}
                  style={{width: 16, height: 16, flex: '0 0 auto'}}
                />
                Has garage
              </label>
            </div>
            <div className="grid-2" style={{marginBottom: 12}}>
              <div className="form-group" style={{marginBottom: 0}}>
                <label className="form-label" htmlFor="prop-status">Status</label>
                <select id="prop-status" className="form-input" value={form.status} onChange={e => setField('status', e.target.value)}>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="sold">Sold</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="form-group" style={{marginBottom: 0}}>
                <label className="form-label" htmlFor="prop-listing-agent-name">Listing Agent Name</label>
                <input id="prop-listing-agent-name" className="form-input" value={form.listing_agent_name} onChange={e => setField('listing_agent_name', e.target.value)} />
              </div>
            </div>
            <div className="form-group" style={{marginBottom: 12}}>
              <label className="form-label" htmlFor="prop-public-remarks">Public Remarks</label>
              <textarea id="prop-public-remarks" className="form-input" rows={3} value={form.public_remarks} onChange={e => setField('public_remarks', e.target.value)} />
            </div>
            </>
            )}
            <div style={editingId ? { border: '1px solid var(--border)', borderRadius: 12, padding: 16, marginTop: 18 } : undefined}>
            {editingId && (
              <div style={{fontWeight: 700, marginBottom: 14, color: '#C2410C'}}>You can edit</div>
            )}
            {editingId && (
              <>
                <div className="grid-2" style={{marginBottom: 12}}>
                  <div className="form-group" style={{marginBottom: 0}}>
                    <label className="form-label" htmlFor="prop-edit-price">List price (CAD)</label>
                    <input id="prop-edit-price" className="form-input" type="number" min="0" value={form.list_price} onChange={e => setField('list_price', e.target.value)} />
                  </div>
                  <div className="form-group" style={{marginBottom: 0}}>
                    <label className="form-label" htmlFor="prop-edit-status">Status</label>
                    <select id="prop-edit-status" className="form-input" value={form.status || 'active'} onChange={e => setField('status', e.target.value)}>
                      {!['active', 'pending', 'sold', 'inactive'].includes(form.status) && form.status && (
                        <option value={form.status}>{form.status}</option>
                      )}
                      <option value="active">Active</option>
                      <option value="pending">Pending</option>
                      <option value="sold">Sold</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>
                <div className="form-group" style={{marginBottom: 16}}>
                  <label className="form-label" htmlFor="prop-edit-remarks">Public remarks</label>
                  <textarea id="prop-edit-remarks" className="form-input" rows={3} value={form.public_remarks} onChange={e => setField('public_remarks', e.target.value)} />
                </div>
              </>
            )}
            <div className="form-group" style={{marginBottom: 16}}>
              <label className="form-label" htmlFor="prop-house-notes">What you know about this home (AI shares it, credited to you)</label>
              <textarea
                id="prop-house-notes"
                className="form-input"
                rows={3}
                value={form.agent_house_notes}
                onChange={e => setField('agent_house_notes', e.target.value)}
                placeholder="Details buyers ask about that aren't in the listing — e.g. roof redone 2021, furnace serviced annually, sellers flexible on closing…"
              />
              <div style={{fontSize: 11, color: 'var(--text-muted)', marginTop: 4}}>
                Shared honestly with buyers when they ask about this home — not a place to hold back
                known issues (disclosure rules still apply to you as the listing agent).
              </div>
            </div>
            <div className="form-group" style={{marginBottom: 16}}>
              <label className="form-label" style={{display: 'flex', alignItems: 'center', gap: 8}}>
                <input
                  type="checkbox"
                  checked={sellerHoursOn}
                  onChange={e => setSellerHoursOn(e.target.checked)}
                  style={{width: 16, height: 16, flex: '0 0 auto'}}
                />
                Seller has set hours for this home
              </label>
              <div style={{fontSize: 11, color: 'var(--text-muted)', marginTop: 4, marginBottom: 8}}>
                Newfoundland time. Ava offers only times that fit both these hours and your showing hours.
                Until this is on, she offers your open times as a request and says you will check with the seller.
              </div>
              {sellerHoursOn && (
                <div style={{display: 'grid', gap: 8}}>
                  {SHOWING_DAYS.map(([key, label]) => (
                    <label key={key} style={{display: 'flex', alignItems: 'center', gap: 10, fontSize: 14}}>
                      <input
                        type="checkbox"
                        checked={sellerHoursForm[key].enabled}
                        onChange={(e) => setSellerHoursForm((prev) => ({
                          ...prev,
                          [key]: { ...prev[key], enabled: e.target.checked },
                        }))}
                        style={{width: 16, height: 16, flex: '0 0 auto'}}
                      />
                      <span style={{width: 36}}>{label}</span>
                      <input
                        type="time"
                        className="form-input"
                        style={{width: 130, padding: '6px 8px'}}
                        disabled={!sellerHoursForm[key].enabled}
                        value={sellerHoursForm[key].start}
                        onChange={(e) => setSellerHoursForm((prev) => ({
                          ...prev,
                          [key]: { ...prev[key], start: e.target.value },
                        }))}
                      />
                      <span>to</span>
                      <input
                        type="time"
                        className="form-input"
                        style={{width: 130, padding: '6px 8px'}}
                        disabled={!sellerHoursForm[key].enabled}
                        value={sellerHoursForm[key].end}
                        onChange={(e) => setSellerHoursForm((prev) => ({
                          ...prev,
                          [key]: { ...prev[key], end: e.target.value },
                        }))}
                      />
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="form-group" style={{marginBottom: 16}}>
              <label className="form-label" htmlFor="prop-showing-availability">Seller note</label>
              <input
                id="prop-showing-availability"
                className="form-input"
                value={form.showing_availability}
                onChange={e => setField('showing_availability', e.target.value)}
                placeholder="e.g. 24h notice, tenant occupied"
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : (editingId ? 'Save Changes' : 'Add Listing')}
            </button>
            </div>
          </form>
        </div>
      )}

      <div className="search-bar">
        <Search size={16} color="var(--text-muted)" />
        <input
          placeholder="Search by address, city, or MLS® number..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      <div style={{display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap'}}>
        <select
          className="form-input"
          style={{width: 'auto', padding: '8px 12px'}}
          value={filter.city}
          onChange={e => setFilter(f => ({...f, city: e.target.value}))}
        >
          <option value="">All Cities</option>
          {allCities.map((city) => (
            <option key={city} value={city}>{city}</option>
          ))}
        </select>
        <select
          className="form-input"
          style={{width: 'auto', padding: '8px 12px'}}
          value={filter.bedrooms}
          onChange={e => setFilter(f => ({...f, bedrooms: e.target.value}))}
        >
          <option value="">Any Bedrooms</option>
          <option value="2">2+</option>
          <option value="3">3+</option>
          <option value="4">4+</option>
        </select>
        <select
          className="form-input"
          style={{width: 'auto', padding: '8px 12px'}}
          value={filter.max_price}
          onChange={e => setFilter(f => ({...f, max_price: e.target.value}))}
        >
          <option value="">Any Price</option>
          {priceBandOptions.map((band) => (
            <option key={band} value={band}>Under {formatPrice(band)}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="empty-state"><div className="spinner" /></div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <p>No properties found matching your search.</p>
        </div>
      ) : (
        <div className="properties-grid">
          {filtered.map(property => (
            <div
              key={property.id}
              className={`property-card${property.is_own_listing ? ' own-listing' : ''}`}
              onClick={property.is_own_listing ? () => openEdit(property) : undefined}
              style={property.is_own_listing ? { cursor: 'pointer' } : undefined}
            >
              <div className="property-image">
                {property.photos?.[0]?.url ? (
                  <img
                    className="property-image__photo"
                    src={property.photos[0].url}
                    alt={property.photos[0].caption || `${property.address_line1}, ${property.city}`}
                    loading="lazy"
                  />
                ) : (
                  <PropertyTypeIcon type={property.property_type} />
                )}
                {property.is_own_listing && (
                  <span className="badge badge-active property-own-badge">My listing</span>
                )}
              </div>
              <div className="property-body">
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4}}>
                  <div className="property-price">{formatPrice(property.list_price)}</div>
                  <span className={`badge badge-${STATUS_BADGE_CLASS[property.status] || 'active'}`}>{property.status}</span>
                </div>
                <div className="property-address">
                  {property.address_line1}, {property.city}
                </div>
                <div className="property-specs">
                  <div className="property-spec">
                    <Bed size={12} /> {property.bedrooms} bed
                  </div>
                  <div className="property-spec">
                    <Bath size={12} /> {property.bathrooms} bath
                  </div>
                  {property.square_feet && (
                    <div className="property-spec">
                      <Square size={12} /> {property.square_feet.toLocaleString()} sqft
                    </div>
                  )}
                  {property.garage && (
                    <div className="property-spec">
                      <Car size={12} /> Garage
                    </div>
                  )}
                </div>
                <div style={{marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <span style={{fontSize: 12, color: 'var(--text-muted)'}}>
                    MLS® {property.mls_number || '—'}
                  </span>
                  {property.is_own_listing && (
                    <span style={{display: 'flex', gap: 8}}>
                      <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); openEdit(property); }} title="Edit listing">
                        <Pencil size={13} /> Edit
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); handleDelete(property); }} title="Delete listing">
                        <Trash2 size={13} />
                      </button>
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {hasMore && !loading && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
          <button type="button" className="btn btn-secondary" onClick={loadMoreProperties} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}
